import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { surveyApi } from '../../api/client';
import type { SurveyDetail } from '../../api/client';

/** 满意度调查填写页（需求书模块七）：评分题(1-5) / 单选题 / 文本题。
 * 入口：服务号群发消息链接 /survey/<id>，或「我的」页待填写提醒。 */
export function SurveyPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState<SurveyDetail | null>(null);
  const [answers, setAnswers] = useState<Record<string, number | string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!id) return;
    surveyApi.get(id)
      .then(data => {
        setSurvey(data);
        if (data.answered) setDone(true);
        setLoading(false);
      })
      .catch(e => {
        setError(e instanceof Error ? e.message : '问卷加载失败');
        setLoading(false);
      });
  }, [id]);

  function setAnswer(key: string, value: number | string) {
    setAnswers(prev => ({ ...prev, [key]: value }));
  }

  async function submit() {
    if (!survey || !id) return;
    for (const q of survey.questions) {
      const v = answers[q.key];
      const empty = v === undefined || (typeof v === 'string' && !v.trim());
      if (empty && q.required !== false) {
        alert(`「${q.title}」为必答题`);
        return;
      }
    }
    setSubmitting(true);
    try {
      await surveyApi.submit(id, answers);
      setDone(true);
    } catch (e) {
      alert(e instanceof Error ? e.message : '提交失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <div className="loading-state"><span className="spinner" /></div>;
  if (error) {
    return (
      <div className="page" style={{ textAlign: 'center', paddingTop: 80 }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
        <p style={{ color: 'var(--gray-500)' }}>{error}</p>
        <button className="btn-primary" style={{ marginTop: 16, padding: '10px 32px' }} onClick={() => navigate('/')}>
          返回首页
        </button>
      </div>
    );
  }
  if (!survey) return null;

  if (done) {
    return (
      <div className="page" style={{ textAlign: 'center', paddingTop: 80 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
        <h1 style={{ fontSize: 20 }}>感谢您的反馈！</h1>
        <p style={{ color: 'var(--gray-500)', marginTop: 8 }}>
          《{survey.title}》已提交，我们会认真改进。
        </p>
        <button className="btn-primary" style={{ marginTop: 24, padding: '12px 40px' }} onClick={() => navigate('/')}>
          返回学习
        </button>
      </div>
    );
  }

  if (survey.status === 'closed') {
    return (
      <div className="page" style={{ textAlign: 'center', paddingTop: 80 }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
        <p style={{ color: 'var(--gray-500)' }}>问卷已截止，感谢关注。</p>
        <button className="btn-primary" style={{ marginTop: 16, padding: '10px 32px' }} onClick={() => navigate('/')}>
          返回首页
        </button>
      </div>
    );
  }

  return (
    <div className="page">
      <h1 style={{ fontSize: 19 }}>{survey.title}</h1>
      {survey.intro && (
        <p style={{ color: 'var(--gray-500)', fontSize: 13, margin: '8px 0 16px' }}>{survey.intro}</p>
      )}
      {survey.questions.map((q, idx) => (
        <div key={q.key} style={{
          background: '#fff', borderRadius: 12, padding: 16, marginBottom: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}>
          <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 12 }}>
            {idx + 1}. {q.title}
            {q.required !== false && <span style={{ color: '#cf1322', marginLeft: 4 }}>*</span>}
          </div>

          {q.type === 'score' && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
              {[1, 2, 3, 4, 5].map(n => {
                const active = answers[q.key] === n;
                return (
                  <button
                    key={n}
                    onClick={() => setAnswer(q.key, n)}
                    style={{
                      flex: 1, height: 44, borderRadius: 8, fontSize: 16, fontWeight: 600,
                      border: active ? '2px solid #1f4e79' : '1px solid #d9d9d9',
                      background: active ? '#1f4e79' : '#fafafa',
                      color: active ? '#fff' : '#333',
                    }}
                  >
                    {n}
                  </button>
                );
              })}
            </div>
          )}

          {q.type === 'single' && (
            <div>
              {(q.options || []).map(opt => {
                const active = answers[q.key] === opt;
                return (
                  <button
                    key={opt}
                    onClick={() => setAnswer(q.key, opt)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '12px 14px', marginBottom: 8, borderRadius: 8, fontSize: 14,
                      border: active ? '2px solid #1f4e79' : '1px solid #d9d9d9',
                      background: active ? '#eaf2fb' : '#fafafa',
                      color: '#333',
                    }}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          )}

          {q.type === 'text' && (
            <textarea
              rows={3}
              placeholder="请输入您的建议（可选）"
              value={(answers[q.key] as string) || ''}
              onChange={e => setAnswer(q.key, e.target.value)}
              maxLength={500}
              style={{
                width: '100%', borderRadius: 8, border: '1px solid #d9d9d9',
                padding: '10px 12px', fontSize: 14, resize: 'none', boxSizing: 'border-box',
              }}
            />
          )}
        </div>
      ))}

      <button
        className="btn-primary"
        disabled={submitting}
        onClick={submit}
        style={{ width: '100%', padding: '14px 0', fontSize: 16, marginTop: 8 }}
      >
        {submitting ? '提交中…' : '提交问卷'}
      </button>
    </div>
  );
}
